import { Typography } from 'antd';
import { isEmpty } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { MemberType, Team, UserType } from 'api/models';
import { Project } from 'api/project';
import { useUser } from 'hooks/use-user';
import { useUsersQuery } from 'queries/members';
import { isValidEmail } from 'utils/is-valid-email';

interface Props {
  project: Project;
  placeholder: Team;
}
interface Result {
  isValidNewOptions: (email: string) => boolean;
  noOptionsMessage: (params) => JSX.Element;
}
export const usePlaceholderValueSelectHelpers = ({
  project,
  placeholder,
}: Props): Result => {
  const {
    user: { userType: loggedInUserType },
  } = useUser();
  const { data: users } = useUsersQuery();
  const allPossibleUsers = useMemo(
    () =>
      (users ?? []).filter(
        (user) =>
          user.userType !== UserType.External ||
          user.company.companyId === project.customer.companyId,
      ),
    [project.customer.companyId, users],
  );
  const isExistingEmailId = useCallback(
    (emailId: string) => {
      return !!allPossibleUsers.find(
        ({ emailId: userEmailId }) =>
          userEmailId.toLowerCase() === emailId.toLowerCase(),
      );
    },
    [allPossibleUsers],
  );
  const noOptionsMessage = useCallback(
    ({ inputValue }) => {
      const input = inputValue ? inputValue.trim() : undefined;

      if (isEmpty(users) && !input) {
        return <FormattedMessage id="no.options" />;
      }

      return (
        <FormattedMessage
          id="no.matches.found"
          values={{
            b: (chunks) => (
              <Typography.Text strong>{input ?? chunks}</Typography.Text>
            ),
          }}
        />
      );
    },
    [users],
  );
  const isValidNewOptions = useCallback(
    (email) => {
      if (placeholder.teamType !== MemberType.External) {
        return false;
      }
      if (loggedInUserType === UserType.Partner) {
        return false;
      }

      return isValidEmail(email) && !isExistingEmailId(email);
    },
    [isExistingEmailId, loggedInUserType, placeholder.teamType],
  );

  return {
    isValidNewOptions,
    noOptionsMessage,
  };
};
